import React, { useEffect, useState, useCallback, useMemo } from "react";
import { API, api } from "../lib/api";
import { PageHeader, PageBody, StatusBadge, EmptyState } from "../components/Shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { ChevronDown, ChevronRight, Plus, Trash2, Pencil, Calculator, ReceiptText, Download, RefreshCw, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const CURRENCIES = ["TRY", "USD", "EUR", "GBP", "JPY", "CNY"];
const ORDER_STATUSES = ["all", "open", "in_progress", "completed", "cancelled"];
const PAYMENT_FILTERS = [
  ["all", "Tümü"],
  ["unpaid", "Ödeme bekleyen (pending + vadesi geçti)"],
  ["pending", "Bekliyor"],
  ["partial", "Kısmen ödendi"],
  ["overdue", "Vadesi geçti"],
  ["paid", "Ödendi"],
  ["cancelled", "İptal"],
];
const PAYMENT_BADGE = {
  pending: { label: "Bekliyor", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  partial: { label: "Kısmi", cls: "bg-blue-100 text-blue-800 border-blue-300" },
  paid: { label: "Ödendi", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  overdue: { label: "Vadesi geçti", cls: "bg-red-100 text-red-800 border-red-300" },
  cancelled: { label: "İptal", cls: "bg-slate-100 text-slate-500 border-slate-300" },
};

const fmtTL = (v) => (v == null || isNaN(v))
  ? "—"
  : `${Number(v).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`;

const fmtRate = (v) => (v == null || isNaN(v))
  ? "—"
  : Number(v).toLocaleString("tr-TR", { minimumFractionDigits: 4, maximumFractionDigits: 4 });

// Trigger a browser download of the Excel export for the current filters.
async function downloadXlsx(filters, apiRoot) {
  const params = new URLSearchParams();
  if (filters.order_status && filters.order_status !== "all") params.set("order_status", filters.order_status);
  if (filters.payment_status && filters.payment_status !== "all") params.set("payment_status", filters.payment_status);
  if (filters.customer_id && filters.customer_id !== "all") params.set("customer_id", filters.customer_id);
  if (filters.date_from) params.set("date_from", filters.date_from);
  if (filters.date_to) params.set("date_to", filters.date_to);
  const url = `${apiRoot}/accounting/orders.xlsx${params.toString() ? `?${params}` : ""}`;
  try {
    const res = await fetch(url, { credentials: "include" });
    if (!res.ok) throw new Error("Download failed");
    const blob = await res.blob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    const stamp = new Date().toISOString().slice(0, 10);
    link.download = `accounting-${stamp}.xlsx`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(link.href), 5000);
    toast.success("Excel exported");
  } catch (e) {
    toast.error("Excel export failed");
  }
}

function KpiCard({ label, value, tone = "default", testid }) {
  const tones = {
    default: "text-slate-900",
    good: "text-emerald-700",
    warn: "text-amber-700",
    bad: "text-red-700",
  };
  return (
    <div className="p-5 border border-slate-300 bg-white" data-testid={testid}>
      <div className="label-caps text-slate-500">{label}</div>
      <div className={`mt-2 font-mono text-2xl tracking-tight ${tones[tone] || tones.default}`}>{value}</div>
    </div>
  );
}

function ExtraCostDialog({ open, onOpenChange, initial, onSave }) {
  const [form, setForm] = useState({ label: "", amount: "", currency: "TRY", tl_rate: "", notes: "" });
  useEffect(() => {
    if (open) {
      setForm({
        label: initial?.label || "",
        amount: initial?.amount != null ? String(initial.amount) : "",
        currency: initial?.currency || "TRY",
        tl_rate: initial?.tl_rate != null ? String(initial.tl_rate) : "",
        notes: initial?.notes || "",
      });
    }
  }, [open, initial]);

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(form.amount);
    if (isNaN(amt)) return toast.error("Amount is required");
    if (form.currency !== "TRY" && !form.tl_rate) return toast.error("TL rate is required for non-TRY currencies");
    await onSave({
      label: form.label.trim() || "Extra cost",
      amount: amt,
      currency: form.currency,
      tl_rate: form.tl_rate === "" ? null : parseFloat(form.tl_rate),
      notes: form.notes || null,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-md">
        <DialogHeader><DialogTitle className="font-display tracking-tight">{initial ? "Edit extra cost" : "New extra cost"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label className="label-caps">Label</Label>
            <Input required className="rounded-none mt-1" placeholder="e.g. Shipping, Loading, Customs"
              value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="ec-label" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-1"><Label className="label-caps">Amount</Label>
              <Input required type="number" step="any" className="rounded-none mt-1"
                value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="ec-amount" />
            </div>
            <div className="col-span-1"><Label className="label-caps">Currency</Label>
              <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                <SelectTrigger className="rounded-none mt-1" data-testid="ec-currency"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-1"><Label className="label-caps">TL rate</Label>
              <Input type="number" step="any" className="rounded-none mt-1"
                disabled={form.currency === "TRY"}
                placeholder={form.currency === "TRY" ? "1.0000" : ""}
                value={form.currency === "TRY" ? "" : form.tl_rate}
                onChange={(e) => setForm({ ...form, tl_rate: e.target.value })} data-testid="ec-tl-rate" />
            </div>
          </div>
          <div><Label className="label-caps">Notes</Label>
            <Input className="rounded-none mt-1" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="ec-notes" />
          </div>
          <DialogFooter>
            <Button type="submit" className="rounded-none bg-blue-600 hover:bg-blue-700" data-testid="ec-save">{initial ? "Save" : "Add"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function OrderDetailPanel({ orderId, onDirty }) {
  const [detail, setDetail] = useState(null);
  const [editECOpen, setEditECOpen] = useState(false);
  const [editingEC, setEditingEC] = useState(null);
  const [bulkSale, setBulkSale] = useState("");
  const [bulkPurchase, setBulkPurchase] = useState("");
  const [bulkOverwrite, setBulkOverwrite] = useState(true);
  const [applying, setApplying] = useState(false);
  const [tcmbCur, setTcmbCur] = useState("USD");
  const [tcmbBusy, setTcmbBusy] = useState(false);
  const [purDate, setPurDate] = useState("");
  const [saleDate, setSaleDate] = useState("");

  useEffect(() => {
    if (detail) {
      setPurDate(detail.purchase_invoice_date || "");
      setSaleDate(detail.sale_invoice_date || "");
    }
  }, [detail?.order_id]);

  const load = useCallback(async () => {
    const { data } = await api.get(`/accounting/orders/${orderId}`);
    setDetail(data);
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  const updateItemRate = async (itemId, patch) => {
    // optimistic local update
    setDetail((d) => d ? {
      ...d,
      items: d.items.map((it) => it.id === itemId ? { ...it, ...patch } : it),
    } : d);
    try {
      await api.put(`/orders/${orderId}/items/${itemId}`, patch);
      await load();
      onDirty?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
      load();
    }
  };

  const saveEC = async (payload) => {
    try {
      if (editingEC) {
        await api.put(`/orders/${orderId}/extra-costs/${editingEC.id}`, payload);
        toast.success("Extra cost updated");
      } else {
        await api.post(`/orders/${orderId}/extra-costs`, payload);
        toast.success("Extra cost added");
      }
      setEditingEC(null);
      await load();
      onDirty?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const delEC = async (cid) => {
    if (!window.confirm("Delete this extra cost?")) return;
    try {
      await api.delete(`/orders/${orderId}/extra-costs/${cid}`);
      toast.success("Extra cost removed");
      await load();
      onDirty?.();
    } catch { toast.error("Failed"); }
  };

  const applyBulkRates = async () => {
    if (!bulkSale && !bulkPurchase) return;
    setApplying(true);
    try {
      const payload = { order_ids: [orderId], overwrite: bulkOverwrite };
      if (bulkSale) payload.sale_tl_rate = parseFloat(bulkSale);
      if (bulkPurchase) payload.purchase_tl_rate = parseFloat(bulkPurchase);
      const { data } = await api.post("/accounting/bulk-tl-rates", payload);
      toast.success(`Applied to ${data.items} line${data.items === 1 ? "" : "s"}`);
      setBulkSale("");
      setBulkPurchase("");
      await load();
      onDirty?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setApplying(false); }
  };

  const fetchTcmb = async () => {
    setTcmbBusy(true);
    try {
      const { data } = await api.post(`/orders/${orderId}/tcmb-fetch`, {
        currency: tcmbCur,
        purchase_invoice_date: purDate || null,
        sale_invoice_date: saleDate || null,
      });
      const msgs = [];
      if (data.purchase?.status === "rate") {
        setBulkPurchase(Number(data.purchase.rate).toFixed(4));
        msgs.push(`Alım (${data.purchase.date}): ${Number(data.purchase.rate).toFixed(4)} ₺`);
      }
      if (data.purchase?.status === "scheduled") {
        msgs.push(`Alım kuru ${data.purchase.date} için planlandı — tarih geldiğinde otomatik dolacak`);
      }
      if (data.sale?.status === "rate") {
        setBulkSale(Number(data.sale.rate).toFixed(4));
        msgs.push(`Satış (${data.sale.date}): ${Number(data.sale.rate).toFixed(4)} ₺`);
      }
      if (data.sale?.status === "scheduled") {
        msgs.push(`Satış kuru ${data.sale.date} için planlandı — tarih geldiğinde otomatik dolacak`);
      }
      toast.success(msgs.join(" · ") || "Tarih girilmedi — güncel kur için tarihleri boş bırakıp tekrar çekin", { duration: 6000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "TCMB kuru alınamadı");
    } finally { setTcmbBusy(false); }
  };

  const saveAccountingNotes = async (patch) => {
    const prev = { invoice_date: detail?.invoice_date, payment_status: detail?.payment_status, accounting_notes: detail?.accounting_notes };
    setDetail((d) => d ? { ...d, ...patch } : d);
    try {
      await api.put(`/orders/${orderId}/accounting-notes`, patch);
      toast.success("Kaydedildi");
      onDirty?.();
    } catch (e) {
      setDetail((d) => d ? { ...d, ...prev } : d);
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    }
  };

  if (!detail) return <div className="px-4 py-6 text-sm text-slate-500 bg-slate-50 border-t border-slate-200">Loading…</div>;

  return (
    <div className="bg-slate-50 border-t border-slate-200 px-6 py-6 space-y-6" data-testid={`accounting-detail-${orderId}`}>
      {/* Per-line TL rate editor */}
      <section>
        <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
          <div>
            <div className="label-caps">Line items · TL rates</div>
            <div className="text-xs text-slate-500 mt-1">Set purchase &amp; sale TL rates per line. Blank rates count as 0 TL for that line.</div>
          </div>
          <div className="flex items-end gap-2 flex-wrap">
            <div>
              <Label className="label-caps text-[10px]">TCMB</Label>
              <div className="flex items-center gap-1 mt-1">
                <Select value={tcmbCur} onValueChange={setTcmbCur}>
                  <SelectTrigger className="h-9 w-20 rounded-none font-mono text-xs" data-testid={`acc-order-tcmb-cur-${orderId}`}><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    {["USD","EUR","GBP","CHF","JPY"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  onClick={fetchTcmb}
                  disabled={tcmbBusy}
                  variant="outline"
                  className="rounded-none h-9 border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                  data-testid={`acc-order-tcmb-fetch-${orderId}`}
                  title="TCMB kurunu çek — tarih girildiyse o tarihin kuru gelir; ileri tarihse tarihi gelince otomatik dolar"
                >
                  <RefreshCw className={`w-4 h-4 mr-1 ${tcmbBusy ? "animate-spin" : ""}`} />
                  {tcmbBusy ? "..." : "Kuru çek"}
                </Button>
              </div>
            </div>
            <div>
              <Label className="label-caps text-[10px]">Alım fat. tarihi</Label>
              <input
                type="date"
                value={purDate}
                onChange={(e) => setPurDate(e.target.value)}
                onBlur={() => { if ((purDate || null) !== (detail.purchase_invoice_date || null)) saveAccountingNotes({ purchase_invoice_date: purDate || null }); }}
                className="h-9 w-36 rounded-none border border-slate-300 px-2 font-mono text-xs bg-white mt-1"
                data-testid={`acc-order-pur-inv-date-${orderId}`}
              />
            </div>
            <div>
              <Label className="label-caps text-[10px]">Satış fat. tarihi</Label>
              <input
                type="date"
                value={saleDate}
                onChange={(e) => setSaleDate(e.target.value)}
                onBlur={() => { if ((saleDate || null) !== (detail.sale_invoice_date || null)) saveAccountingNotes({ sale_invoice_date: saleDate || null }); }}
                className="h-9 w-36 rounded-none border border-slate-300 px-2 font-mono text-xs bg-white mt-1"
                data-testid={`acc-order-sale-inv-date-${orderId}`}
              />
            </div>
            <div>
              <Label className="label-caps text-[10px]">Sale TL rate</Label>
              <Input type="number" step="any" placeholder="e.g. 35.0000"
                value={bulkSale} onChange={(e) => setBulkSale(e.target.value)}
                className="w-28 h-9 rounded-none font-mono text-xs mt-1"
                data-testid={`acc-order-bulk-sale-${orderId}`}
              />
            </div>
            <div>
              <Label className="label-caps text-[10px]">Purchase TL rate</Label>
              <Input type="number" step="any" placeholder="e.g. 33.0000"
                value={bulkPurchase} onChange={(e) => setBulkPurchase(e.target.value)}
                className="w-28 h-9 rounded-none font-mono text-xs mt-1"
                data-testid={`acc-order-bulk-purchase-${orderId}`}
              />
            </div>
            <div className="flex items-center gap-1.5 pb-2">
              <Checkbox id={`bulk-ow-${orderId}`} checked={bulkOverwrite}
                onCheckedChange={(v) => setBulkOverwrite(!!v)}
                data-testid={`acc-order-bulk-overwrite-${orderId}`}
              />
              <Label htmlFor={`bulk-ow-${orderId}`} className="label-caps text-[10px] cursor-pointer whitespace-nowrap">
                Overwrite existing
              </Label>
            </div>
            <Button
              onClick={applyBulkRates}
              disabled={applying || (!bulkSale && !bulkPurchase)}
              className="rounded-none h-9 bg-blue-600 hover:bg-blue-700"
              data-testid={`acc-order-bulk-apply-${orderId}`}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${applying ? "animate-spin" : ""}`} />
              {applying ? "Applying…" : "Apply to all lines"}
            </Button>
          </div>
        </div>
        {(detail.pending_tl_rates?.purchase || detail.pending_tl_rates?.sale) && (
          <div className="mb-3 border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid={`acc-pending-rates-${orderId}`}>
            {detail.pending_tl_rates?.purchase && (
              <div>Alım kuru planlandı: <b>{detail.pending_tl_rates.purchase.date}</b> ({detail.pending_tl_rates.purchase.currency}) — tarih geldiğinde otomatik doldurulacak</div>
            )}
            {detail.pending_tl_rates?.sale && (
              <div>Satış kuru planlandı: <b>{detail.pending_tl_rates.sale.date}</b> ({detail.pending_tl_rates.sale.currency}) — tarih geldiğinde otomatik doldurulacak</div>
            )}
          </div>
        )}
        <div className="bg-white border border-slate-300 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 border-b border-slate-300"><tr className="text-left">
              <th className="py-2 px-3 label-caps">Part #</th>
              <th className="py-2 px-3 label-caps text-right">Qty</th>
              <th className="py-2 px-3 label-caps text-right">Unit Sale</th>
              <th className="py-2 px-3 label-caps text-right">Unit Purchase</th>
              <th className="py-2 px-3 label-caps text-right">Sale TL Rate</th>
              <th className="py-2 px-3 label-caps text-right">Purchase TL Rate</th>
              <th className="py-2 px-3 label-caps text-right">Sale Line TL</th>
              <th className="py-2 px-3 label-caps text-right">Purchase Line TL</th>
            </tr></thead>
            <tbody>
              {detail.items.map((it) => (
                <tr key={it.id} className="border-b border-slate-200" data-testid={`acc-item-row-${it.id}`}>
                  <td className="py-2 px-3">
                    <div className="font-mono font-medium">{it.part_number}</div>
                    <div className="text-xs text-slate-500">{it.manufacturer || ""}</div>
                  </td>
                  <td className="py-2 px-3 font-mono text-right">{it.quantity}</td>
                  <td className="py-2 px-3 font-mono text-right">{it.unit_price} {it.currency}</td>
                  <td className="py-2 px-3 font-mono text-right">{it.purchase_price ?? "—"} {it.purchase_currency || it.currency}</td>
                  <td className="py-2 px-3 text-right">
                    <input
                      type="number" step="any"
                      defaultValue={it.sale_tl_rate ?? ""}
                      key={`s-${it.id}-${it.sale_tl_rate ?? ""}`}
                      onBlur={(e) => {
                        const v = e.target.value === "" ? null : parseFloat(e.target.value);
                        if (v !== it.sale_tl_rate) updateItemRate(it.id, { sale_tl_rate: v });
                      }}
                      placeholder="—"
                      className="w-24 h-8 rounded-none border border-slate-300 px-2 text-right font-mono text-xs bg-white"
                      data-testid={`acc-item-sale-rate-${it.id}`}
                    />
                  </td>
                  <td className="py-2 px-3 text-right">
                    <input
                      type="number" step="any"
                      defaultValue={it.purchase_tl_rate ?? ""}
                      key={`p-${it.id}-${it.purchase_tl_rate ?? ""}`}
                      onBlur={(e) => {
                        const v = e.target.value === "" ? null : parseFloat(e.target.value);
                        if (v !== it.purchase_tl_rate) updateItemRate(it.id, { purchase_tl_rate: v });
                      }}
                      placeholder="—"
                      className="w-24 h-8 rounded-none border border-slate-300 px-2 text-right font-mono text-xs bg-white"
                      data-testid={`acc-item-purchase-rate-${it.id}`}
                    />
                  </td>
                  <td className="py-2 px-3 font-mono text-right">{fmtTL(it.sale_line_tl)}</td>
                  <td className="py-2 px-3 font-mono text-right">{fmtTL(it.purchase_line_tl)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Extra costs */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="label-caps">Extra costs</div>
            <div className="text-xs text-slate-500 mt-1">Shipping, loading, customs, etc. Foreign currency? Provide TL rate.</div>
          </div>
          <Button
            className="rounded-none bg-blue-600 hover:bg-blue-700 text-white"
            onClick={() => { setEditingEC(null); setEditECOpen(true); }}
            data-testid={`acc-add-ec-${orderId}`}
          >
            <Plus className="w-4 h-4 mr-2" /> Add cost
          </Button>
        </div>
        {(detail.extra_costs || []).length === 0 ? (
          <div className="bg-white border border-slate-300 p-6 text-center text-sm text-slate-500">No extra costs yet.</div>
        ) : (
          <div className="bg-white border border-slate-300 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 border-b border-slate-300"><tr className="text-left">
                <th className="py-2 px-3 label-caps">Label</th>
                <th className="py-2 px-3 label-caps text-right">Amount</th>
                <th className="py-2 px-3 label-caps">Currency</th>
                <th className="py-2 px-3 label-caps text-right">TL Rate</th>
                <th className="py-2 px-3 label-caps text-right">TL Value</th>
                <th className="py-2 px-3 label-caps">Notes</th>
                <th className="py-2 px-3 w-24 text-right label-caps">Actions</th>
              </tr></thead>
              <tbody>
                {(detail.extra_costs || []).map((c) => {
                  const tlVal = c.currency === "TRY" ? c.amount : (c.tl_rate != null ? c.amount * c.tl_rate : null);
                  return (
                    <tr key={c.id} className="border-b border-slate-200">
                      <td className="py-2 px-3 font-medium">{c.label}</td>
                      <td className="py-2 px-3 font-mono text-right">{c.amount}</td>
                      <td className="py-2 px-3 font-mono">{c.currency}</td>
                      <td className="py-2 px-3 font-mono text-right">{c.currency === "TRY" ? "1.0000" : fmtRate(c.tl_rate)}</td>
                      <td className="py-2 px-3 font-mono text-right">{fmtTL(tlVal)}</td>
                      <td className="py-2 px-3 text-slate-500">{c.notes || "—"}</td>
                      <td className="py-2 px-3 text-right">
                        <div className="inline-flex gap-2">
                          <button className="text-slate-400 hover:text-blue-700" title="Edit"
                            onClick={() => { setEditingEC(c); setEditECOpen(true); }}
                            data-testid={`acc-edit-ec-${c.id}`}
                          ><Pencil className="w-4 h-4" /></button>
                          <button className="text-slate-400 hover:text-red-600" title="Delete"
                            onClick={() => delEC(c.id)}
                            data-testid={`acc-del-ec-${c.id}`}
                          ><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Order-level tax breakdown */}
      <section>
        <div className="label-caps mb-2">Tax breakdown · this order</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard label="Sale TL" value={fmtTL(detail.sale_tl)} testid={`acc-det-sale-tl-${orderId}`} />
          <KpiCard label="Purchase TL" value={fmtTL(detail.purchase_tl)} testid={`acc-det-purchase-tl-${orderId}`} />
          <KpiCard label={`Sale KDV (${Math.round((detail.vat_rate || 0.2) * 100)}%)`} value={fmtTL(detail.sale_kdv)} />
          <KpiCard label={`Purchase KDV (${Math.round((detail.vat_rate || 0.2) * 100)}%)`} value={fmtTL(detail.purchase_kdv)} />
          <KpiCard label="KDV Tax (owed)" value={fmtTL(detail.kdv_tax)} tone={detail.kdv_tax >= 0 ? "warn" : "good"} testid={`acc-det-kdv-${orderId}`} />
          <KpiCard label="Extra Costs" value={fmtTL(detail.extra_costs_tl)} tone="warn" />
          <KpiCard label={`Income Tax (${Math.round((detail.income_tax_rate || 0.25) * 100)}%)`} value={fmtTL(detail.income_tax)} tone="bad" testid={`acc-det-income-tax-${orderId}`} />
          <KpiCard label="Net Profit TL" value={fmtTL(detail.net_profit_tl)} tone={detail.net_profit_tl >= 0 ? "good" : "bad"} testid={`acc-det-net-${orderId}`} />
        </div>
      </section>

      <AccountingNotesSection detail={detail} onSave={saveAccountingNotes} />

      <ExtraCostDialog
        open={editECOpen}
        onOpenChange={(v) => { setEditECOpen(v); if (!v) setEditingEC(null); }}
        initial={editingEC}
        onSave={saveEC}
      />
    </div>
  );
}

function AccountingNotesSection({ detail, onSave }) {
  const PAYMENT_STATUSES = [
    ["", "—"],
    ["pending", "Bekliyor"],
    ["partial", "Kısmen ödendi"],
    ["paid", "Ödendi"],
    ["overdue", "Vadesi geçti"],
    ["cancelled", "İptal"],
  ];
  return (
    <section>
      <div className="label-caps mb-2">Fatura &amp; Ödeme</div>
      <div className="grid md:grid-cols-3 gap-3">
        <div>
          <Label className="label-caps text-[10px]">Fatura tarihi</Label>
          <input
            type="date"
            defaultValue={detail.invoice_date || ""}
            key={`inv-${detail.order_id}-${detail.invoice_date || ""}`}
            onBlur={(e) => {
              const v = e.target.value || null;
              if (v !== (detail.invoice_date || null)) onSave({ invoice_date: v });
            }}
            className="w-full h-9 rounded-none border border-slate-300 px-2 font-mono text-xs bg-white mt-1"
            data-testid={`acc-invoice-date-${detail.order_id}`}
          />
        </div>
        <div>
          <Label className="label-caps text-[10px]">Ödeme durumu</Label>
          <select
            defaultValue={detail.payment_status || ""}
            key={`ps-${detail.order_id}-${detail.payment_status || ""}`}
            onChange={(e) => {
              const v = e.target.value || null;
              if (v !== (detail.payment_status || null)) onSave({ payment_status: v });
            }}
            className="w-full h-9 rounded-none border border-slate-300 px-2 text-xs bg-white mt-1"
            data-testid={`acc-payment-status-${detail.order_id}`}
          >
            {PAYMENT_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div>
          <Label className="label-caps text-[10px]">Muhasebe notu</Label>
          <input
            type="text"
            defaultValue={detail.accounting_notes || ""}
            key={`an-${detail.order_id}-${detail.accounting_notes || ""}`}
            onBlur={(e) => {
              const v = e.target.value.trim() || null;
              if (v !== (detail.accounting_notes || null)) onSave({ accounting_notes: v });
            }}
            placeholder="örn. e-fatura #ABC-123, 30 gün vade, dekont alındı…"
            className="w-full h-9 rounded-none border border-slate-300 px-2 text-xs bg-white mt-1"
            data-testid={`acc-notes-${detail.order_id}`}
          />
        </div>
      </div>
    </section>
  );
}

export default function Accounting() {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [rates, setRates] = useState({ vat_rate: 0.20, income_tax_rate: 0.25 });
  const [expanded, setExpanded] = useState({});
  const [filters, setFilters] = useState({ order_status: "all", payment_status: "all", customer_id: "all", date_from: "", date_to: "" });
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [kdvOpen, setKdvOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.order_status && filters.order_status !== "all") params.order_status = filters.order_status;
      if (filters.payment_status && filters.payment_status !== "all") params.payment_status = filters.payment_status;
      if (filters.customer_id && filters.customer_id !== "all") params.customer_id = filters.customer_id;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const { data } = await api.get("/accounting/orders", { params });
      setRows(data.orders || []);
      setTotals(data.totals || null);
      setRates({ vat_rate: data.vat_rate, income_tax_rate: data.income_tax_rate });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load accounting data");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = (oid) => setExpanded((e) => ({ ...e, [oid]: !e[oid] }));

  const kpis = useMemo(() => {
    if (!totals) return [];
    const vatPct = Math.round((rates.vat_rate || 0.2) * 100);
    const incPct = Math.round((rates.income_tax_rate || 0.25) * 100);
    return [
      { label: "Sale TL", value: fmtTL(totals.sale_tl), tone: "default", testid: "kpi-sale-tl" },
      { label: "Purchase TL", value: fmtTL(totals.purchase_tl), tone: "default", testid: "kpi-purchase-tl" },
      { label: "Extra Costs TL", value: fmtTL(totals.extra_costs_tl), tone: "warn", testid: "kpi-extra-tl" },
      { label: `KDV Tax (${vatPct}%)`, value: fmtTL(totals.kdv_tax), tone: totals.kdv_tax >= 0 ? "warn" : "good", testid: "kpi-kdv-tax" },
      { label: `Income Tax (${incPct}%)`, value: fmtTL(totals.income_tax), tone: "bad", testid: "kpi-income-tax" },
      { label: "Net Profit TL", value: fmtTL(totals.net_profit_tl), tone: totals.net_profit_tl >= 0 ? "good" : "bad", testid: "kpi-net-profit" },
    ];
  }, [totals, rates]);

  return (
    <>
      <PageHeader
        title="Accounting"
        subtitle="TL profitability · VAT · Income tax"
        testid="accounting-title"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-2 text-xs text-slate-500 mr-2">
              <ReceiptText className="w-4 h-4" />
              KDV {Math.round((rates.vat_rate || 0.2) * 100)}% · Gelir Vergisi {Math.round((rates.income_tax_rate || 0.25) * 100)}%
            </div>
            <Button
              variant="outline"
              className="rounded-none h-9"
              onClick={() => setKdvOpen(true)}
              data-testid="acc-kdv-btn"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" /> KDV Declaration
            </Button>
            <Button
              className="rounded-none h-9 bg-blue-600 hover:bg-blue-700"
              onClick={() => downloadXlsx({ ...filters }, API)}
              data-testid="acc-export-xlsx-btn"
            >
              <Download className="w-4 h-4 mr-2" /> Export Excel
            </Button>
          </div>
        }
      />
      <PageBody>
        {/* Filters */}
        <div className="grid md:grid-cols-5 gap-3 mb-6 p-4 bg-white border border-slate-300">
          <div>
            <Label className="label-caps">Order status</Label>
            <Select value={filters.order_status} onValueChange={(v) => setFilters({ ...filters, order_status: v })}>
              <SelectTrigger className="rounded-none mt-1" data-testid="acc-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">{ORDER_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-caps">Ödeme durumu</Label>
            <Select value={filters.payment_status} onValueChange={(v) => setFilters({ ...filters, payment_status: v })}>
              <SelectTrigger className="rounded-none mt-1" data-testid="acc-filter-payment"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">
                {PAYMENT_FILTERS.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`acc-filter-payment-${v}`}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-caps">Customer</Label>
            <Select value={filters.customer_id} onValueChange={(v) => setFilters({ ...filters, customer_id: v })}>
              <SelectTrigger className="rounded-none mt-1" data-testid="acc-filter-customer"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="all">all</SelectItem>
                {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-caps">Date from</Label>
            <Input type="date" className="rounded-none mt-1" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="acc-filter-date-from" />
          </div>
          <div>
            <Label className="label-caps">Date to</Label>
            <Input type="date" className="rounded-none mt-1" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="acc-filter-date-to" />
          </div>
        </div>

        {/* KPI cards */}
        {totals && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
            {kpis.map((k) => <KpiCard key={k.label} label={k.label} value={k.value} tone={k.tone} testid={k.testid} />)}
          </div>
        )}

        {/* Orders table */}
        {loading ? (
          <div className="p-6 text-center text-slate-500 bg-white border border-slate-300">Loading…</div>
        ) : rows.length === 0 ? (
          <EmptyState title="No orders match these filters" hint="Adjust the filters above or create an order to start seeing accounting data." icon={Calculator} />
        ) : (
          <div className="bg-white border border-slate-300 overflow-x-auto" data-testid="accounting-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-300"><tr className="text-left">
                <th className="py-2 px-3 w-8"></th>
                <th className="py-2 px-3 label-caps">Order #</th>
                <th className="py-2 px-3 label-caps">Customer</th>
                <th className="py-2 px-3 label-caps">Status</th>
                <th className="py-2 px-3 label-caps">Ödeme / Fatura</th>
                <th className="py-2 px-3 label-caps text-right">Sale TL</th>
                <th className="py-2 px-3 label-caps text-right">Purchase TL</th>
                <th className="py-2 px-3 label-caps text-right">Extra TL</th>
                <th className="py-2 px-3 label-caps text-right">KDV Tax</th>
                <th className="py-2 px-3 label-caps text-right">Income Tax</th>
                <th className="py-2 px-3 label-caps text-right">Net Profit TL</th>
              </tr></thead>
              <tbody>
                {rows.map((o) => {
                  const isOpen = !!expanded[o.order_id];
                  return (
                    <React.Fragment key={o.order_id}>
                      <tr
                        className={`border-b border-slate-200 hover:bg-slate-50 cursor-pointer ${isOpen ? "bg-blue-50/40" : ""}`}
                        onClick={() => toggle(o.order_id)}
                        data-testid={`accounting-row-${o.order_id}`}
                      >
                        <td className="py-2 px-3 text-slate-500">
                          {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </td>
                        <td className="py-2 px-3 font-mono font-medium">{o.order_number}</td>
                        <td className="py-2 px-3">{o.customer_name}</td>
                        <td className="py-2 px-3"><StatusBadge status={o.status} /></td>
                        <td className="py-2 px-3" data-testid={`acc-payment-cell-${o.order_id}`}>
                          {o.payment_status ? (
                            <span className={`inline-block px-2 py-0.5 border text-[10px] font-medium uppercase tracking-wider ${PAYMENT_BADGE[o.payment_status]?.cls || "bg-slate-100 text-slate-600 border-slate-300"}`}>
                              {PAYMENT_BADGE[o.payment_status]?.label || o.payment_status}
                            </span>
                          ) : <span className="text-slate-300">—</span>}
                          {o.invoice_date && <div className="text-[10px] text-slate-500 font-mono mt-0.5" data-testid={`acc-invoice-cell-${o.order_id}`}>{o.invoice_date}</div>}
                        </td>
                        <td className="py-2 px-3 font-mono text-right">{fmtTL(o.sale_tl)}</td>
                        <td className="py-2 px-3 font-mono text-right">{fmtTL(o.purchase_tl)}</td>
                        <td className="py-2 px-3 font-mono text-right text-amber-700">{fmtTL(o.extra_costs_tl)}</td>
                        <td className={`py-2 px-3 font-mono text-right ${o.kdv_tax >= 0 ? "text-amber-700" : "text-emerald-700"}`}>{fmtTL(o.kdv_tax)}</td>
                        <td className="py-2 px-3 font-mono text-right text-red-700">{fmtTL(o.income_tax)}</td>
                        <td className={`py-2 px-3 font-mono text-right font-semibold ${o.net_profit_tl >= 0 ? "text-emerald-700" : "text-red-700"}`}>{fmtTL(o.net_profit_tl)}</td>
                      </tr>
                      {isOpen && (
                        <tr>
                          <td colSpan={11} className="p-0">
                            <OrderDetailPanel orderId={o.order_id} onDirty={load} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
              {totals && (
                <tfoot className="bg-slate-100 border-t-2 border-slate-400">
                  <tr>
                    <td colSpan={5} className="py-3 px-3 font-mono text-right label-caps">TOTALS</td>
                    <td className="py-3 px-3 font-mono text-right">{fmtTL(totals.sale_tl)}</td>
                    <td className="py-3 px-3 font-mono text-right">{fmtTL(totals.purchase_tl)}</td>
                    <td className="py-3 px-3 font-mono text-right text-amber-700">{fmtTL(totals.extra_costs_tl)}</td>
                    <td className={`py-3 px-3 font-mono text-right ${totals.kdv_tax >= 0 ? "text-amber-700" : "text-emerald-700"}`}>{fmtTL(totals.kdv_tax)}</td>
                    <td className="py-3 px-3 font-mono text-right text-red-700">{fmtTL(totals.income_tax)}</td>
                    <td className={`py-3 px-3 font-mono text-right font-bold ${totals.net_profit_tl >= 0 ? "text-emerald-700" : "text-red-700"}`}>{fmtTL(totals.net_profit_tl)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </PageBody>

      <KdvDeclarationDialog open={kdvOpen} onOpenChange={setKdvOpen} vatRate={rates.vat_rate} apiRoot={API} />
    </>
  );
}

function KdvDeclarationDialog({ open, onOpenChange, vatRate, apiRoot }) {
  const monthStart = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`; };
  const monthEnd = () => { const d = new Date(); const last = new Date(d.getFullYear(), d.getMonth() + 1, 0); return last.toISOString().slice(0, 10); };
  const [dateFrom, setDateFrom] = useState(monthStart());
  const [dateTo, setDateTo] = useState(monthEnd());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const compute = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/accounting/kdv-declaration", { params: { date_from: dateFrom, date_to: dateTo } });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to compute KDV declaration");
    } finally { setLoading(false); }
  };

  const quickPeriod = (kind, offset = 0) => {
    const now = new Date();
    let from, to;
    if (kind === "month") {
      const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
      from = new Date(d.getFullYear(), d.getMonth(), 1);
      to = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    } else if (kind === "quarter") {
      const currentQ = Math.floor(now.getMonth() / 3);
      const q = currentQ + offset;
      const yearShift = Math.floor(q / 4);
      const qi = ((q % 4) + 4) % 4;
      from = new Date(now.getFullYear() + yearShift, qi * 3, 1);
      to = new Date(now.getFullYear() + yearShift, qi * 3 + 3, 0);
    } else if (kind === "year") {
      from = new Date(now.getFullYear() + offset, 0, 1);
      to = new Date(now.getFullYear() + offset, 11, 31);
    }
    setDateFrom(from.toISOString().slice(0, 10));
    setDateTo(to.toISOString().slice(0, 10));
  };

  useEffect(() => {
    if (open) { setData(null); }
  }, [open]);

  const totals = data?.totals;
  const vatPct = Math.round((vatRate || 0.2) * 100);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-3xl">
        <DialogHeader><DialogTitle className="font-display tracking-tight">KDV Beyannamesi · VAT declaration</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="label-caps">Date from</Label>
              <Input type="date" className="rounded-none mt-1" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="kdv-date-from" />
            </div>
            <div>
              <Label className="label-caps">Date to</Label>
              <Input type="date" className="rounded-none mt-1" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="kdv-date-to" />
            </div>
            <div className="flex items-end gap-1 pb-[2px]">
              <Button type="button" variant="outline" className="rounded-none h-9" onClick={() => quickPeriod("month", 0)}>This month</Button>
              <Button type="button" variant="outline" className="rounded-none h-9" onClick={() => quickPeriod("month", -1)}>Last month</Button>
              <Button type="button" variant="outline" className="rounded-none h-9" onClick={() => quickPeriod("quarter", 0)}>This quarter</Button>
              <Button type="button" variant="outline" className="rounded-none h-9" onClick={() => quickPeriod("year", 0)}>This year</Button>
            </div>
            <Button type="button" onClick={compute} disabled={loading} className="rounded-none h-9 bg-blue-600 hover:bg-blue-700" data-testid="kdv-compute-btn">
              {loading ? "Computing…" : "Compute"}
            </Button>
          </div>

          {totals && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiCard label="Sale TL" value={fmtTL(totals.sale_tl)} />
                <KpiCard label="Purchase TL" value={fmtTL(totals.purchase_tl)} />
                <KpiCard label={`Sale KDV (${vatPct}%)`} value={fmtTL(totals.sale_kdv)} tone="warn" />
                <KpiCard label={`Purchase KDV (${vatPct}%)`} value={fmtTL(totals.purchase_kdv)} tone="good" />
              </div>
              <div className="p-4 bg-slate-900 text-white flex items-center justify-between" data-testid="kdv-net-total">
                <div>
                  <div className="text-xs text-slate-300 label-caps">Net KDV to pay</div>
                  <div className="text-xs text-slate-400 mt-1">Sale KDV − Purchase KDV</div>
                </div>
                <div className={`font-mono text-2xl tracking-tight ${totals.net_kdv >= 0 ? "text-amber-300" : "text-emerald-300"}`}>{fmtTL(totals.net_kdv)}</div>
              </div>
              <div className="border border-slate-300 bg-white max-h-72 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 border-b border-slate-300 sticky top-0"><tr className="text-left">
                    <th className="py-2 px-3 label-caps">Order #</th>
                    <th className="py-2 px-3 label-caps">Customer</th>
                    <th className="py-2 px-3 label-caps">Date</th>
                    <th className="py-2 px-3 label-caps text-right">Sale KDV</th>
                    <th className="py-2 px-3 label-caps text-right">Purchase KDV</th>
                    <th className="py-2 px-3 label-caps text-right">KDV Tax</th>
                  </tr></thead>
                  <tbody>
                    {data.orders.map((o) => (
                      <tr key={o.order_id} className="border-b border-slate-100">
                        <td className="py-2 px-3 font-mono">{o.order_number}</td>
                        <td className="py-2 px-3">{o.customer_name}</td>
                        <td className="py-2 px-3 font-mono text-xs">{o.date}</td>
                        <td className="py-2 px-3 font-mono text-right">{fmtTL(o.sale_kdv)}</td>
                        <td className="py-2 px-3 font-mono text-right">{fmtTL(o.purchase_kdv)}</td>
                        <td className={`py-2 px-3 font-mono text-right ${o.kdv_tax >= 0 ? "text-amber-700" : "text-emerald-700"}`}>{fmtTL(o.kdv_tax)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end">
                <Button type="button" className="rounded-none bg-slate-800 hover:bg-slate-900 text-white"
                  onClick={() => downloadXlsx({ date_from: dateFrom, date_to: dateTo }, apiRoot)}>
                  <Download className="w-4 h-4 mr-2" /> Download period as Excel
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
